import {
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { MaterialModule } from 'src/app/material.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { AuthService } from '@features/auth/services/auth.service';
import { AuthShowcaseAsideComponent } from '@features/auth/presentation/components/auth-showcase-aside/auth-showcase-aside.component';
import { AuthScrollOnFocusDirective } from '@features/auth/presentation/utils/auth-scroll-on-focus.directive';
import {
  authErrorI18nKey,
  resolveAuthRequestErrorKind,
} from '@features/auth/utils/auth-request-error.util';
import { OtpCodeInputComponent } from '@shared/ui/otp-code-input/otp-code-input.component';
import {
  OTP_CODE_LENGTH,
  normalizeOtpDigits,
} from '@shared/utils/otp-code-digits.util';
import {
  formatOtpWait,
  isLongOtpResendWait,
} from '@shared/utils/format-otp-wait.util';

const EMAIL_FORMAT_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

@Component({
  selector: 'app-reset-password',
  imports: [
    RouterModule,
    MaterialModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    TablerIconsModule,
    OtpCodeInputComponent,
    AuthShowcaseAsideComponent,
    AuthScrollOnFocusDirective,
  ],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly translate = inject(TranslateService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly otpCodeLength = OTP_CODE_LENGTH;
  readonly otpResetToken = signal(0);
  readonly otpRetryAfter = signal(0);
  readonly code = signal('');

  hidePassword = true;
  hideConfirmPassword = true;
  isSubmitting = false;
  submitted = false;
  errorMessage = '';
  validationAttempted = false;
  /** Llegó desde forgot-password con email: mostrar banner de código enviado. */
  codeSentHint = false;

  form = new FormGroup(
    {
      email: new FormControl('', [
        Validators.required,
        Validators.pattern(EMAIL_FORMAT_PATTERN),
      ]),
      password: new FormControl('', [
        Validators.required,
        Validators.minLength(8),
        Validators.pattern(/[A-Z]/),
        Validators.pattern(/[a-z]/),
        Validators.pattern(/[0-9]/),
      ]),
      passwordConfirmation: new FormControl('', [Validators.required]),
    },
    {
      validators: (group) => {
        const password = group.get('password')?.value ?? '';
        const confirmation = group.get('passwordConfirmation')?.value ?? '';
        return password === confirmation ? null : { passwordMismatch: true };
      },
    }
  );

  readonly passwordRules = ['length', 'uppercase', 'lowercase', 'number'] as const;

  private otpCooldownTimer: ReturnType<typeof setInterval> | null = null;

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => this.clearCooldown());
    const emailParam = this.route.snapshot.queryParamMap.get('email')?.trim();
    if (emailParam) {
      this.form.patchValue({ email: emailParam });
      this.codeSentHint = true;
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { email: null },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }
  }

  get f() {
    return this.form.controls;
  }

  passwordMeets(rule: 'length' | 'uppercase' | 'lowercase' | 'number'): boolean {
    const password = this.form.controls.password.value ?? '';
    return {
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
    }[rule];
  }

  passwordIsValid(): boolean {
    return this.passwordRules.every((rule) => this.passwordMeets(rule));
  }

  showPasswordRules(): boolean {
    if (this.passwordIsValid()) {
      return false;
    }
    const ctrl = this.form.controls.password;
    const value = ctrl.value ?? '';
    return value.length > 0 || ctrl.dirty || ctrl.touched || this.validationAttempted;
  }

  otpResendWaitKey(): string {
    return isLongOtpResendWait(this.otpRetryAfter())
      ? 'auth.resetPassword.resendWaitLong'
      : 'auth.resetPassword.resendWait';
  }

  otpResendWaitLabel(): string {
    return formatOtpWait(this.otpRetryAfter());
  }

  onCodeChange(value: string): void {
    const normalized = normalizeOtpDigits(value, OTP_CODE_LENGTH);
    this.code.set(normalized);
    if (this.errorMessage && normalized.length < OTP_CODE_LENGTH) {
      this.errorMessage = '';
    }
  }

  resendCode(): void {
    if (this.otpRetryAfter() > 0 || this.isSubmitting) {
      return;
    }
    const email = (this.form.value.email ?? '').trim();
    if (!email || this.form.controls.email.invalid) {
      this.validationAttempted = true;
      this.form.controls.email.markAsTouched();
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.authService
      .requestPasswordReset(email)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.otpResetToken.update((n) => n + 1);
          this.code.set('');
          this.codeSentHint = true;
          this.startCooldown(60);
        },
        error: (err: unknown) => {
          const kind = resolveAuthRequestErrorKind(err);
          this.errorMessage = this.translate.instant(authErrorI18nKey('auth.forgotPassword', kind));
        },
      });
  }

  submit(): void {
    this.validationAttempted = true;
    const code = this.code();
    if (this.form.invalid || code.length !== OTP_CODE_LENGTH || this.isSubmitting) {
      this.form.markAllAsTouched();
      if (code.length !== OTP_CODE_LENGTH) {
        this.errorMessage = this.translate.instant('auth.resetPassword.codeRequired');
      }
      return;
    }

    this.errorMessage = '';
    this.isSubmitting = true;

    const email = (this.form.value.email ?? '').trim();
    const password = this.form.value.password ?? '';
    const passwordConfirmation = this.form.value.passwordConfirmation ?? '';

    this.authService
      .resetPassword({ email, code, password, passwordConfirmation })
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.submitted = true;
        },
        error: (err: unknown) => {
          const kind = resolveAuthRequestErrorKind(err);
          this.errorMessage = this.translate.instant(authErrorI18nKey('auth.resetPassword', kind));
          this.otpResetToken.update((n) => n + 1);
          this.code.set('');
        },
      });
  }

  goToLogin(): void {
    void this.router.navigate(['/authentication/login'], { replaceUrl: true });
  }

  private startCooldown(seconds: number): void {
    this.clearCooldown();
    this.otpRetryAfter.set(Math.max(0, Math.floor(seconds)));
    if (this.otpRetryAfter() <= 0) {
      return;
    }
    this.otpCooldownTimer = setInterval(() => {
      const next = this.otpRetryAfter() - 1;
      this.otpRetryAfter.set(Math.max(0, next));
      if (next <= 0) {
        this.clearCooldown();
      }
    }, 1000);
  }

  private clearCooldown(): void {
    if (this.otpCooldownTimer) {
      clearInterval(this.otpCooldownTimer);
      this.otpCooldownTimer = null;
    }
  }
}
